/**
 * State badges for plans and containers. A container's badge is its DAY as
 * it really stands (the Live card and the board read the same facts): not
 * generated yet → waiting → sending automatically / running → done, or
 * stopped / paused / auto-send halted — never "dry run" once drafts exist.
 * The colours say how far the day is (26/10/2026, user: "the done / send
 * automatically shouldnt be on the same color because its confusing"), as
 * the Live strip's: green = finished (✓ done — the Board's sent lines carry
 * the same green ✓), solid blue = auto-send at work, light blue = still
 * going otherwise (scheduled, running, waiting for a person),
 * amber = held or stopped, grey = not started or off.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { CheckCircle2 } from 'lucide-react';

import { Badge } from '@/components/ui';
import { dkLabel } from '../../utils/clock/dk-label';
import { minutesToHHMM } from '../../utils/format';
import type { PlanStatus } from '../../utils/plan-status';
import type { ContainerLiveFacts } from '../live/live-status';

/** Phase 9 (Phase 20: per container — its campaign group's, then its own
 *  layer's) — auto-send as the day shows it: sending, shadow (Phase 29 —
 *  decides like a send, sends nothing), or halted (a stop-loss, the technical
 *  halt, kill or a disabled plan); null = a person sends. */
export type LiveAutoSend = 'sending' | 'shadow' | 'halted' | null;

/** HH:mm on the Copenhagen clock. */
export const cphTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Copenhagen',
  });

export const PlanStatusBadge: FC<{
  status: PlanStatus;
  recipientTimezone: string | null;
  /** 19/10/2026 — the country header: its Send window tile says the times, the badge says the plan's state. */
  short?: boolean;
}> = ({ status, recipientTimezone, short = false }) => {
  switch (status.kind) {
    case 'no-plan':
      return <Badge tone="secondary">no plan yet</Badge>;
    case 'killed':
      return <Badge tone="destructive">KILLED — nothing generates or sends</Badge>;
    case 'disabled':
      return <Badge tone="secondary">disabled</Badge>;
    case 'waiting': {
      if (short) return <Badge tone="info">on · waiting for the window</Badge>;
      // Danish first (15/10/2026): "window opens 23:00 🇩🇰 (08:00 local)".
      const local = minutesToHHMM(status.opensAtMin ?? 0);
      const dk = dkLabel(status.opensAtMin ?? 0, recipientTimezone)?.slice(0, 5);
      return <Badge tone="info">{dk ? `window opens ${dk} 🇩🇰 (${local} local)` : `window opens ${local}`}</Badge>;
    }
    case 'window-open':
      return <Badge tone="success">{short ? 'on · window open' : 'window open'}</Badge>;
    default:
      return <Badge tone="success">enabled</Badge>;
  }
};

/** A container's day as it really stands (deriveContainerLive's facts). */
export const ContainerDayBadge: FC<{
  facts: ContainerLiveFacts;
  autoSend: LiveAutoSend;
}> = ({ facts, autoSend }) => {
  const open = facts.state === 'running' || facts.state === 'waiting' || facts.state === 'scheduled';
  if (open && facts.scheduled && autoSend !== 'halted') return <Badge tone="info">{facts.scheduled} scheduled in SMSWiser</Badge>;
  if (open && autoSend === 'sending') return <Badge tone="default">sending automatically</Badge>;
  if (open && autoSend === 'shadow') return <Badge tone="warning">simulated — says what it would send</Badge>;
  if (open && autoSend === 'halted') return <Badge tone="warning">auto-send halted</Badge>;
  switch (facts.state) {
    case 'paused':
      return <Badge tone="warning">paused — pool exhausted</Badge>;
    case 'stopped':
      return <Badge tone="warning">stopped for today</Badge>;
    case 'done':
      return (
        <Badge tone="success">
          <CheckCircle2 className="mr-1 h-3 w-3" aria-hidden />
          done{facts.doneAt ? ` 🇩🇰 ${cphTime(facts.doneAt)}` : ''}
        </Badge>
      );
    case 'running':
      return <Badge tone="info">running</Badge>;
    case 'waiting':
      // Light blue like "running": solid blue is auto-send at work.
      return <Badge tone="info">generated — waiting</Badge>;
    default:
      return <Badge tone="secondary">not generated yet</Badge>;
  }
};
